import { FRAME_MAX } from "./BoatTree.ts";

/**
 * The effects tree: art that plays **on top of** the game rather than in it — a muzzle flash,
 * and whatever joins it. Stored as `config/fx.json`, written by the FX panel of the dashboard
 * (`editor/panels/FxPanel.tsx`).
 *
 * Like [BoatTree.ts](BoatTree.ts) this file holds the **schema only** — nothing is read from
 * disk here, so it imports no JSON and compiles for the browser, for Node and for the editor
 * alike. And like the boat tree it is a sibling of `Settings.ts` rather than a panel inside it:
 * an effect is a piece of art with a couple of numbers hanging off it, which is not something
 * `SettingField` can describe.
 *
 * **It is keyed by art, not by a list of entries.** Which effects exist is a question the `fxA`
 * sheet answers — the panel reads the sheet's animation names and shows every one — and a stored
 * list would be a second, staler copy of that. So the file holds only what the art cannot say for
 * itself: how big each one is, how fast it plays and how often it comes up. Art added to the
 * sheet turns up in the panel by itself, at the defaults, waiting to be authored.
 *
 * **One record per kind of effect**, keyed by the prefix its art is named with — `muzzle_` for
 * the fire out of a gun, `water_splash` for a ball falling into the sea. They are separate
 * records rather than one because they are fitted to different things and therefore measured in
 * different units: a flash belongs to a gun and is sized in gun lengths, a splash belongs to the
 * sea and is sized in pixels. A single record would have to carry both and leave half of it
 * meaningless on every entry.
 *
 * **The client plays them; the arena knows nothing about them.** A flash is smoke over a shot
 * the server has already resolved, so unlike the boat tree this is not sync-critical and two
 * clients disagreeing about flames would cost nothing. They do not disagree anyway: the draw is
 * seeded off the shot's own id, so every screen shows the same flame for the same bang without a
 * byte on the wire. See `MuzzleFlashes` in src/scripts/boats/MuzzleFlashes.ts.
 */

/**
 * How one flash is fitted to the gun that makes it.
 *
 * Two numbers rather than an offset as well, because the third is already authored elsewhere:
 * **the flash is pinned to the gun's projectile spawn point** — `muzzleX`/`muzzleY` on the
 * `CannonType` — and pinned there by the flash's own pivot, the `anchor` TexturePacker stored
 * on every frame of the `fxA` sheet. The ball and the fire come out of the same hole, so saying
 * so once is better than authoring two points and keeping them together by hand.
 */
export interface MuzzleFlash {
    /**
     * How long the flash is, as a multiple of the gun's **drawn** length — `CannonType.size`
     * times the slot's `scale`, the same unit the spawn point is measured in. So one number
     * authored against the art holds at every rung of the gun ladder and in every slot: a gun
     * scaled up gets a bigger flash without anybody typing a second number.
     *
     * The flash's own length is the long side of its **untrimmed** box, taken from the first
     * frame of the animation — the same rule a sail is scaled by, and for the same reason: the
     * frames of one flash are wildly different sizes as it blooms and dies, and scaling each of
     * them to fit would make the effect pulse instead of grow.
     *
     * `shared/` never sees a texture, so nothing here can turn this into pixels. Whatever draws
     * the flash does the division, exactly as it does for `muzzleX`.
     */
    scale: number;
    /**
     * How fast the flash plays, in **frames per second**. Independent of the render frame rate:
     * whatever draws it steps the frames off elapsed seconds, the way the sails are
     * stepped (`WAKE_FPS`, `SAIL_FPS` in Boat.ts), so a flash lasts the same time on a 30Hz
     * laptop as on a 144Hz monitor.
     *
     * It is per flash rather than one constant for the sheet because the animations are not the
     * same length — nine frames of shotgun against ten of tank — and "how long is this bang" is a
     * judgement about the art, not about the game. `frames / fps` is how long one flash lives,
     * which is the number the effect is actually felt as.
     */
    fps: number;
    /**
     * How often this flash comes up when a gun fires, relative to the other flashes. Weights are
     * relative to each other rather than to any total: three flashes at 100 is an even draw, and
     * so is three at 7.
     *
     * **A weight of 0 takes a flash out of the draw without deleting it**, which is how a piece
     * of art is retired while its authored size and heading are kept. That also makes all-zero
     * the off switch for the whole effect — unlike the sea life weights, which fall back to an
     * even mix, because the school has a `count` to switch it off with and this has nothing.
     */
    weight: number;
    /**
     * Which way the flash points, in **degrees** clockwise, turned about its own pivot. `0` is
     * the art as the sheet holds it.
     *
     * It exists because the sheet is not authored to one heading: `muzzle_basic` blooms east off
     * a pivot on its left, and `muzzle_shotgun` and `muzzle_tank` blow west off pivots on their
     * right. A gun points **north** in its own space — the zero a slot's `rotation` and a ship's
     * `heading` both use — so those are `-90` and `+90` respectively, and neither is a number
     * anybody could have guessed without looking at the picture. Hence a dial, and a preview
     * with a cannon under it.
     *
     * Degrees rather than the radians the renderer runs on, like every other angle in `config/`.
     */
    rotation: number;
}

/**
 * How one water splash is fitted: the white ring a spent cannonball throws up when it runs out of
 * range and drops into the sea.
 *
 * **It has no rotation and no pivot**, which is the whole difference from a `MuzzleFlash`. A
 * splash is drawn top-down and radially symmetric — a foam disc blooming out of the point of
 * impact — so its registration point is the centre of its own box by construction, and there is
 * no heading for a dial to name. Whatever draws it spins it by a random angle instead, so a run
 * of them does not read as the same stamp pressed nine times.
 */
export interface Splash {
    /**
     * How wide the splash is drawn, in **world pixels** — its diameter, edge to edge, measured on
     * the long side of its first frame's untrimmed box.
     *
     * Pixels rather than a multiple of something, unlike `MuzzleFlash.scale`: a flash belongs to
     * a gun and grows with it, and a splash belongs to the sea, which has no size to be a
     * fraction of. The number is judged against a hull — 190px at level 1 — which is why the
     * dashboard draws one beside it.
     */
    size: number;
    /** How fast it plays, in frames per second. Same rules as `MuzzleFlash.fps`. */
    fps: number;
    /** How often this splash comes up against the others. Same rules as `MuzzleFlash.weight`. */
    weight: number;
}

export interface FxTree {
    /**
     * One entry per muzzle flash, keyed by its **art reference** — the name of an animation in
     * the `fxA` sheet, e.g. `muzzle_basic`. Art with no entry draws at `MUZZLE_FLASH_DEFAULT`.
     */
    muzzleFlashes: Record<string, MuzzleFlash>;
    /**
     * One entry per water splash, keyed the same way — e.g. `water_splashA`. Art with no entry
     * draws at `SPLASH_DEFAULT`.
     */
    splashes: Record<string, Splash>;
}

/** The sheet the effects live in. The panel offers this sheet's animations and nothing else. */
export const FX_SHEET = "fxA";

/**
 * What makes an animation in that sheet one kind of effect rather than another: the prefix its
 * name starts with. This is the whole routing rule — a category's roster is every animation in
 * the sheet under its prefix, so art joins a category by being named for it and nothing else.
 */
export const MUZZLE_PREFIX = "muzzle_";
export const SPLASH_PREFIX = "water_splash";

/** Ceiling on how many entries a stored file may carry, so a corrupt one cannot allocate freely. */
export const MAX_FX_ENTRIES = 64;

/**
 * Bounds for every number in the tree, declared once so the coercion and the editor's boxes
 * cannot disagree. Values are clamped to the range but never snapped to the step, exactly as in
 * `Settings.ts` and `BoatTree.ts`.
 */
export const FX_LIMITS = {
    /** Up to eight gun lengths: a broadside from a swivel gun is mostly smoke and flame. */
    flashScale: { min: 0.05, max: 8, step: 0.05 },
    flashRotation: { min: -180, max: 180, step: 5 },
    /** 1fps is a slideshow and 120 is over in a tenth of a second. Both ends are useful. */
    flashFps: { min: 1, max: 120, step: 1 },
    /** Zero is "never draw this one". No ceiling worth defending — weights are relative. */
    flashWeight: { min: 0, max: 1000, step: 10 },
    /** In world pixels against a 190px hull. 400 is a splash twice the length of a ship. */
    splashSize: { min: 10, max: 400, step: 5 }
} as const;

/**
 * What a flash the file says nothing about draws as: its own art, its own size against the gun,
 * unturned. Deliberately **not** a guess at a heading — a flash lying on its side is obviously
 * unauthored, where one silently rotated to a plausible north is a bug nobody looks for.
 */
export const MUZZLE_FLASH_DEFAULT: MuzzleFlash = { scale: 1, rotation: 0, fps: 24, weight: 100 };

/**
 * What a splash the file says nothing about draws as.
 *
 * 60px is a third of a hull and a good deal more than the 18px ball that made it: a spent shot
 * dropping short should be legible as *something happened over there* without being mistaken for
 * a hit, which is drawn as a ring twice this wide (`SPLASH_RADIUS_END` in ShotField.ts).
 */
export const SPLASH_DEFAULT: Splash = { size: 60, fps: 24, weight: 100 };

/**
 * The shipped tree. The three flashes in `fxA`, turned onto the gun's north: `muzzle_basic`
 * blooms east off its pivot and the other two blow west off theirs, which is the whole argument
 * for `rotation` being a dial.
 */
export const FX_DEFAULTS: FxTree = {
    muzzleFlashes: {
        muzzle_basic: { scale: 1, rotation: -90, fps: 24, weight: 100 },
        muzzle_shotgun: { scale: 1, rotation: 90, fps: 24, weight: 100 },
        muzzle_tank: { scale: 1, rotation: 90, fps: 24, weight: 100 }
    },
    splashes: {
        water_splashA: { size: 60, fps: 24, weight: 100 }
    }
};

type Limit = { min: number; max: number };

/** A finite number, clamped into a limit. Anything unusable falls back to the default. */
function num(raw: unknown, limit: Limit, fallback: number): number {
    const value = Number(raw);
    if (!Number.isFinite(value)) return fallback;
    return Math.min(limit.max, Math.max(limit.min, value));
}

function coerceMuzzleFlash(raw: unknown): MuzzleFlash {
    const source = (raw ?? {}) as Partial<Record<keyof MuzzleFlash, unknown>>;
    return {
        scale: num(source.scale, FX_LIMITS.flashScale, MUZZLE_FLASH_DEFAULT.scale),
        rotation: num(source.rotation, FX_LIMITS.flashRotation, MUZZLE_FLASH_DEFAULT.rotation),
        fps: num(source.fps, FX_LIMITS.flashFps, MUZZLE_FLASH_DEFAULT.fps),
        weight: num(source.weight, FX_LIMITS.flashWeight, MUZZLE_FLASH_DEFAULT.weight)
    };
}

function coerceSplash(raw: unknown): Splash {
    const source = (raw ?? {}) as Partial<Record<keyof Splash, unknown>>;
    return {
        size: num(source.size, FX_LIMITS.splashSize, SPLASH_DEFAULT.size),
        fps: num(source.fps, FX_LIMITS.flashFps, SPLASH_DEFAULT.fps),
        weight: num(source.weight, FX_LIMITS.flashWeight, SPLASH_DEFAULT.weight)
    };
}

/**
 * One category's record, coerced. Anything that is not an object of entries reads as the shipped
 * defaults for that category, so a file holding only `muzzleFlashes` still arrives with the
 * splashes the repo ships rather than with an empty sea.
 */
function coerceRecord<T>(raw: unknown, each: (entry: unknown) => T, shipped: Record<string, T>): Record<string, T> {
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return { ...shipped };

    const record: Record<string, T> = {};
    for (const [name, entry] of Object.entries(raw).slice(0, MAX_FX_ENTRIES)) {
        const key = name.trim().slice(0, FRAME_MAX);
        if (key.length > 0) record[key] = each(entry);
    }

    return record;
}

/**
 * Turns anything at all into a usable `FxTree`, the same way `coerceBoatTree` does for the boat:
 * unknown shapes replaced with defaults, numbers clamped, the record capped. It runs on **both**
 * ends — the editor's API before writing, the game on load.
 *
 * The one rule the other panels do not have: **keys are kept as they are found.** Every other
 * `coerce*` in the repo drops what it does not recognise, because every other file's keys are
 * fields this code knows the names of. These are art references, and `shared/` cannot see a
 * sheet to check them against — so an entry for art that has been renamed is carried through
 * rather than quietly deleted, and the panel is where you find out it points at nothing.
 *
 * A missing file reads as `FX_DEFAULTS` rather than as an empty record: the three flashes in the
 * sheet are authored, and a fresh checkout should draw them the way the repo does.
 */
export function coerceFx(raw: unknown): FxTree {
    const source = (raw ?? {}) as Partial<Record<keyof FxTree, unknown>>;

    return {
        muzzleFlashes: coerceRecord(source.muzzleFlashes, coerceMuzzleFlash, FX_DEFAULTS.muzzleFlashes),
        splashes: coerceRecord(source.splashes, coerceSplash, FX_DEFAULTS.splashes)
    };
}

/** What one piece of flash art is fitted at. Art the file says nothing about takes the default. */
export function muzzleFlashOf(tree: FxTree, name: string): MuzzleFlash {
    return tree.muzzleFlashes[name] ?? { ...MUZZLE_FLASH_DEFAULT };
}

/** The same for a splash. */
export function splashOf(tree: FxTree, name: string): Splash {
    return tree.splashes[name] ?? { ...SPLASH_DEFAULT };
}

/**
 * Which of `names` an effect uses this time, drawn against the weights `weightOf` reports.
 *
 * `roll` is a number in `[0, 1)` and the caller provides it, which is what keeps this a pure
 * function of the tree and checkable without stubbing `Math.random`. The client seeds it off the
 * shot's id rather than rolling fresh, so every screen watching the same event draws the same art.
 *
 * `null` when nothing can be drawn: no names, or every weight at zero. That is a legitimate
 * authored state and the off switch for a whole category, so a caller must treat it as "nothing
 * this time" rather than as an error. Art the tree says nothing about is weighted at its
 * category's default, so a piece dropped into the sheet joins the rotation unauthored.
 */
function pickWeighted(names: readonly string[], weightOf: (name: string) => number, roll: number): string | null {
    let total = 0;
    for (const name of names) total += weightOf(name);
    if (total <= 0) return null;

    // A roll outside [0, 1) would otherwise fall off the end of the loop and return null, which
    // would read as "nothing authored" rather than as the caller's bad number.
    let at = Math.min(Math.max(roll, 0), 0.999999) * total;

    for (const name of names) {
        at -= weightOf(name);
        if (at < 0) return name;
    }

    // Only reachable on floating-point crumbs at the very top of the range.
    return names[names.length - 1] ?? null;
}

/** Which flash a gun makes this time. See `pickWeighted`. */
export function pickMuzzleFlash(tree: FxTree, names: readonly string[], roll: number): string | null {
    return pickWeighted(names, (name) => muzzleFlashOf(tree, name).weight, roll);
}

/** Which splash a spent ball throws up this time. See `pickWeighted`. */
export function pickSplash(tree: FxTree, names: readonly string[], roll: number): string | null {
    return pickWeighted(names, (name) => splashOf(tree, name).weight, roll);
}

/** How long one effect lasts, in seconds, given how many frames its art has. `0` if it has none. */
export function effectSeconds(effect: { fps: number }, frames: number): number {
    return effect.fps > 0 ? frames / effect.fps : 0;
}
